import React, { useState, useMemo, useCallback, useEffect } from "react";
import Modal, { ModalButton, ModalFooter } from "../../common/Modal";
import ConfirmationModal from "../../common/ConfirmationModal";
import type { IPermission } from "../../../types/rbac";
import { PermissionNodeType } from "../../../types/rbac";
import PermissionTree from "./PermissionTree";
import { RoleRecord, RoleType } from "./RoleCards";
import { TextArea } from "../../common/TextArea";
import { Input, Select, RadioButton, Toggle, Loading, SearchInput } from "../../common";
import { SelectOption } from "../../common/Select";
import { Network, ChevronRight, ChevronDown, Check } from "lucide-react";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import PermissionGate from "../../common/PermissionGate";


const flattenActionIds = (node: IPermission): string[] => {
  if (!node) return [];
  if (node.type === PermissionNodeType.ACTION) return [node.id];
  return (node.children || []).flatMap(flattenActionIds);
};

const getModules = (tree: IPermission[]): IPermission[] => {
  const root = tree.find(
    (n) => n.type === PermissionNodeType.ROOT
  );
  if (root?.children?.length) return root.children;
  return tree.filter(
    (n) => n.type === PermissionNodeType.MODULE
  );
};

const collectAllActionIds = (modules: IPermission[]): string[] => {
  return modules.flatMap(flattenActionIds);
};

const collectSelectedActionIds = (
  tree: IPermission[],
  selected: Set<string>,
): string[] => {
  const ids: string[] = [];
  const visit = (n: IPermission) => {
    if (selected.has(n.id)) {
      ids.push(...flattenActionIds(n));
      return;
    }
    (n.children || []).forEach(visit);
  };
  tree.forEach(visit);
  return Array.from(new Set(ids));
};

const actionIdsToNodeIds = (tree: IPermission[], actionIds: string[]): string[] => {
  const actionSet = new Set(actionIds);
  const result = new Set<string>();

  const walk = (nodes: IPermission[]) => {
    for (const node of nodes) {
      if (node.type === PermissionNodeType.ACTION) continue;
      const children = node.children || [];
      const subModules = children.filter(
        (ch) => ch.type !== PermissionNodeType.ACTION
      );
      if (subModules.length === 0) {
        const nodeActions = flattenActionIds(node);
        if (nodeActions.some((id) => actionSet.has(id))) {
          result.add(node.id);
        }
      } else {
        walk(subModules);
      }
    }
  };

  walk(tree);
  return Array.from(result);
};

type ModuleToggleListProps = {
  tree: IPermission[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  lockedIds?: Set<string>;
};

const ModuleToggleList: React.FC<ModuleToggleListProps> = ({
  tree,
  selectedIds,
  onChange,
  disabled,
  lockedIds,
}) => {
  const modules = useMemo(() => getModules(tree), [tree]);
  const [expandedModules, setExpandedModules] = React.useState<Set<string>>(
    new Set()
  );

  const selectedSet = useMemo(
    () => new Set(selectedIds.map(String)),
    [selectedIds]
  );

  const lockedSet = useMemo(
    () => lockedIds || new Set<string>(),
    [lockedIds]
  );

  const toggleModule = useCallback(
    (moduleId: string, enable: boolean) => {
      const module = modules.find((m) => m.id === moduleId);
      if (!module) return;
      const actionIds = flattenActionIds(module);
      const unlockedIds = actionIds.filter((id) => !lockedSet.has(id));
      if (unlockedIds.length === 0) return;
      const newSet = new Set(selectedSet);
      if (enable) {
        unlockedIds.forEach((id) => newSet.add(id));
      } else {
        unlockedIds.forEach((id) => newSet.delete(id));
      }
      onChange(Array.from(newSet));
    },
    [modules, selectedSet, onChange, lockedSet]
  );

  const isModuleFullySelected = useCallback(
    (moduleId: string): boolean => {
      const module = modules.find((m) => m.id === moduleId);
      if (!module) return false;
      const actionIds = flattenActionIds(module);
      if (actionIds.length === 0) return false;
      return actionIds.every((id) => selectedSet.has(id));
    },
    [modules, selectedSet]
  );

  const isModulePartiallySelected = useCallback(
    (moduleId: string): boolean => {
      const module = modules.find((m) => m.id === moduleId);
      if (!module) return false;
      const actionIds = flattenActionIds(module);
      if (actionIds.length === 0) return false;
      const anySelected = actionIds.some((id) => selectedSet.has(id));
      return anySelected && !isModuleFullySelected(moduleId);
    },
    [modules, selectedSet, isModuleFullySelected]
  );

  const isAllLocked = useCallback(
    (moduleId: string): boolean => {
      const module = modules.find((m) => m.id === moduleId);
      if (!module) return false;
      const actionIds = flattenActionIds(module);
      if (actionIds.length === 0) return false;
      return actionIds.every((id) => lockedSet.has(id));
    },
    [modules, lockedSet]
  );

  const totalActionCount = useMemo(
    () => collectAllActionIds(modules).length,
    [modules]
  );

  const toggleExpand = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  if (modules.length === 0) {
    return (
      <div className="text-sm text-[var(--color-text-tertiary)] text-center py-8">
        No modules available.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-[var(--color-text-tertiary)]">
          {selectedIds.length} of {totalActionCount} actions selected
        </span>
      </div>
      <div className="divide-y divide-[var(--color-border-primary)] border border-[var(--color-border-primary)] rounded-[12px] overflow-hidden">
        {modules.map((module) => {
          const isExpanded = expandedModules.has(module.id);
          const isFullySelected = isModuleFullySelected(module.id);
          const isPartiallySelected = isModulePartiallySelected(module.id);
          const actionCount = flattenActionIds(module).length;
          const allLocked = isAllLocked(module.id);

          const hasSubmodules = (module.children || []).some(
            (ch) => ch.type !== PermissionNodeType.ACTION
          );

          return (
            <div key={module.id}>
              <div
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  allLocked ? "" : "hover:bg-[var(--color-bg-muted)]"
                } ${
                  hasSubmodules
                    ? `cursor-pointer ${isExpanded ? "bg-[var(--color-bg-muted)]" : ""}`
                    : ""
                }`}
                onClick={() => hasSubmodules && toggleExpand(module.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    allLocked ? "text-[var(--color-text-tertiary)]" : "text-[var(--color-text-primary)]"
                  }`}>
                    {module.name}
                    {allLocked && (
                      <span className="inline-flex items-center align-middle ml-1.5 text-[var(--color-text-tertiary)]">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </span>
                    )}
                    {hasSubmodules && (
                      <span className="inline-flex items-center align-middle ml-1.5 text-[var(--color-text-tertiary)]">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[var(--color-text-tertiary)]">
                    {actionCount} action{actionCount === 1 ? "" : "s"}
                  </p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Toggle
                    checked={isFullySelected}
                    onChange={(checked) => toggleModule(module.id, checked)}
                    disabled={disabled || allLocked}
                    size="sm"
                  />
                </div>
              </div>
              {hasSubmodules && isExpanded && (
                <div className="border-t border-[var(--color-border-primary)]">
                  {module.children
                    .filter((ch) => ch.type !== PermissionNodeType.ACTION)
                    .map((submodule) => {
                      const subActionIds = flattenActionIds(submodule);
                      const subSelected = subActionIds.every((id) =>
                        selectedSet.has(id)
                      );
                      const subLocked = subActionIds.every((id) =>
                        lockedSet.has(id)
                      );
                      const subUnlockedIds = subActionIds.filter(
                        (id) => !lockedSet.has(id)
                      );
                      return (
                        <div
                          key={submodule.id}
                          className="flex items-center gap-3 px-4 py-2.5 pl-12"
                        >
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${
                              subLocked
                                ? "text-[var(--color-text-tertiary)]"
                                : "text-[var(--color-text-primary)]"
                            }`}>
                              {submodule.name}
                              {subLocked && (
                                <span className="inline-flex items-center align-middle ml-1.5 text-[var(--color-text-tertiary)]">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-[var(--color-text-tertiary)]">
                              {subActionIds.length} action
                              {subActionIds.length === 1 ? "" : "s"}
                            </p>
                          </div>
                          <Toggle
                            checked={subSelected}
                            onChange={(checked) => {
                              if (subLocked) return;
                              const newSet = new Set(selectedSet);
                              if (checked) {
                                subUnlockedIds.forEach((id) => newSet.add(id));
                              } else {
                                subUnlockedIds.forEach((id) => newSet.delete(id));
                              }
                              onChange(Array.from(newSet));
                            }}
                            disabled={disabled || subLocked}
                            size="sm"
                          />
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export type RoleFormData = {
  name: string;
  type: RoleType | "";
  isActive: boolean;
  description: string;
  permissionIds: string[];
};

type CreateRoleModalProps = {
  isOpen: boolean;
  tree: IPermission[];
  formData: RoleFormData;
  onChange: (data: Partial<RoleFormData>) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving: boolean;
  isEditing?: boolean;
  roleTypeOptions: SelectOption[];
};

export const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  isOpen,
  tree,
  formData,
  onChange,
  onSubmit,
  onClose,
  saving,
  isEditing = false,
  roleTypeOptions,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={isEditing ? "Edit Role" : "Create Role"}
    size="xl"
    footer={
      <ModalFooter>
        <ModalButton variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </ModalButton>
        <ModalButton
          variant="primary"
          onClick={onSubmit}
          loading={saving}
          disabled={saving}
        >
          {saving ? (isEditing ? "Updating..." : "Saving...") : isEditing ? "Save Changes" : "Create Role"}
        </ModalButton>
      </ModalFooter>
    }
  >
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Role Name"
          required
          placeholder="Enter role name"
          value={formData.name}
          onChange={(value) => onChange({ name: value as string })}
        />
        <Select
          label="Role Type"
          required
          options={roleTypeOptions}
          value={formData.type}
          onChange={(value) =>
            onChange({ type: (value as RoleType) || "" })
          }
          placeholder="Select type"
          clearable
        />
      </div>
      <div>
        <RadioButton
          name="roleActive"
          label="Status"
          required
          value={formData.isActive ? "active" : "inactive"}
          onChange={(val) => onChange({ isActive: val === "active" })}
          options={[
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
          ]}
        />
      </div>
      <div className="space-y-2">
        <TextArea
          label="Description"
          placeholder="Optional notes about this role"
          value={formData.description}
          onChange={(value) => onChange({ description: value })}
          minRows={3}
        />
      </div>
      <div className="border-t border-[var(--color-border-primary)] pt-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
              Module Permissions
            </h4>
            <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
              Toggle modules on/off to grant or revoke all actions under each module.
            </p>
          </div>
        </div>
        <ModuleToggleList
          tree={tree}
          selectedIds={formData.permissionIds}
          onChange={(ids) => onChange({ permissionIds: ids })}
          disabled={saving}
        />
      </div>
    </div>
  </Modal>
);

type MapUsersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  userOptions: SelectOption[];
  roleOptions: SelectOption[];
  selectedUserIds: string[];
  selectedUserId: string | undefined;
  setSelectedUserid: (value: string | undefined) => void;
  selectedRoleId: string | undefined;
  onUserChange: (value: string[]) => void;
  onRoleChange: (value: string) => void;
  onReset: () => void;
  onAssign: () => void;
  isBulkAssignLoading: boolean;
  canAssign: boolean;
  rolePrefillLoading: boolean;
  userRoleMap?: Record<string, string>;
};

export const MapUsersModal: React.FC<MapUsersModalProps> = ({
  isOpen,
  onClose,
  userOptions,
  roleOptions,
  selectedUserIds,
  selectedRoleId,
  onUserChange,
  onRoleChange,
  onReset,
  onAssign,
  isBulkAssignLoading,
  canAssign,
  rolePrefillLoading,
  userRoleMap,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!selectedRoleId || !userRoleMap) return;
    const assigned = Object.entries(userRoleMap)
      .filter(([, roleId]) => roleId === selectedRoleId)
      .map(([userId]) => userId);
    onUserChange(assigned);
  }, [selectedRoleId, userRoleMap, onUserChange]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return userOptions;
    const q = searchQuery.toLowerCase();
    return userOptions.filter((u) =>
      u.label.toLowerCase().includes(q),
    );
  }, [userOptions, searchQuery]);

  const toggleUser = useCallback(
    (userId: string) => {
      const isSelected = selectedUserIds.includes(userId);
      if (isSelected) {
        onUserChange(selectedUserIds.filter((id) => id !== userId));
      } else {
        onUserChange([...selectedUserIds, userId]);
      }
    },
    [selectedUserIds, onUserChange],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Map Users to Role"
      size="xl"
      bodyClassName="min-h-[480px]"
    >
      <PermissionGate code={PERMISSIONS.ACCESS_RIGHT_MANAGE}>
        <div className="space-y-4">
          <Select
            label="Select Role"
            options={roleOptions}
            value={selectedRoleId}
            onChange={(value) => onRoleChange(value ? String(value) : "")}
            required
            placeholder="Search roles..."
            searchable
            clearable
            loading={rolePrefillLoading}
          />

          <div>
            <SearchInput
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              placeholder="Search employees..."
            />
          </div>

          <div className="border border-[var(--color-border-primary)] rounded-[12px] divide-y divide-[var(--color-border-primary)] max-h-[280px] overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="text-sm text-[var(--color-text-tertiary)] text-center py-8">
                No employees found.
              </div>
            ) : (
              filteredUsers.map((user) => {
                const checked = selectedUserIds.includes(String(user.value));
                return (
                  <div
                    key={user.value}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                      checked
                        ? "bg-[var(--color-bg-muted)]"
                        : "hover:bg-[var(--color-bg-muted)]"
                    }`}
                    onClick={() => toggleUser(String(user.value))}
                  >
                    <div className="w-8 h-8 rounded-full bg-[var(--color-bg-muted)] flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                        {(user.label?.charAt(0) || "?").toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                        {user.label}
                      </p>
                    </div>
                    {checked ? (
                      <span className="w-5 h-5 rounded-full bg-[var(--color-primary-600)] flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    ) : (
                      <span className="w-5 h-5 rounded-full border-2 border-[var(--color-border-primary)] flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <ModalButton variant="secondary" onClick={onReset} disabled={isBulkAssignLoading}>
              Reset
            </ModalButton>
            <ModalButton
              variant="primary"
              onClick={onAssign}
              loading={isBulkAssignLoading}
              disabled={!canAssign}
            >
              {isBulkAssignLoading ? "Assigning..." : "Assign Role"}
            </ModalButton>
          </div>
        </div>
      </PermissionGate>
    </Modal>
  );
};

type AdditionalPermissionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  userOptions: SelectOption[];
  selectedUserId: string | undefined;
  onUserChange: (value: string) => void;
  overridesLoading: boolean;
  overridesSaving: boolean;
  tree: IPermission[];
  overrideUiNodeIds: string[];
  onOverrideUiNodeIdsChange: (ids: string[]) => void;
  overrideLockedIds: Set<string>;
  onSave: () => void;
  canSave: boolean;
};

export const AdditionalPermissionsModal: React.FC<AdditionalPermissionsModalProps> = ({
  isOpen,
  onClose,
  userOptions,
  selectedUserId,
  onUserChange,
  overridesLoading,
  overridesSaving,
  tree,
  overrideUiNodeIds,
  onOverrideUiNodeIdsChange,
  overrideLockedIds,
  onSave,
  canSave,
}) => {
  const lockedActionIds = useMemo(
    () => new Set(collectSelectedActionIds(tree, new Set(overrideLockedIds))),
    [tree, overrideLockedIds]
  );

  const selectedActionIds = useMemo(
    () => collectSelectedActionIds(tree, new Set(overrideUiNodeIds)),
    [tree, overrideUiNodeIds]
  );

  const handleModuleChange = useCallback(
    (actionIds: string[]) => {
      const nodeIds = actionIdsToNodeIds(tree, actionIds);
      onOverrideUiNodeIdsChange(nodeIds);
    },
    [tree, onOverrideUiNodeIdsChange]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Additional User Permissions"
      size="xl"
      bodyClassName="min-h-[480px]"
      footer={
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose} disabled={overridesSaving}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={onSave}
            loading={overridesSaving}
            disabled={!canSave}
          >
            {overridesSaving ? "Saving..." : "Save"}
          </ModalButton>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <Select
          label="Select Employee"
          required
          options={userOptions}
          value={selectedUserId}
          onChange={(value) => onUserChange(value ? String(value) : "")}
          placeholder="Search Employee..."
          searchable
          clearable
        />
        {!selectedUserId ? (
          <div className="text-sm text-[var(--color-text-tertiary)] text-center py-8">
            Select an employee to manage their additional permissions.
          </div>
        ) : overridesLoading ? (
          <div className="border border-[var(--color-border-primary)] rounded-[16px] p-8 flex justify-center">
            <Loading size="md" centered />
          </div>
        ) : (
          <div className="border-t border-[var(--color-border-primary)] pt-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  Module Permissions
                </h4>
                <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                  Locked permissions are inherited from the user&rsquo;s role and cannot be changed.
                </p>
              </div>
            </div>
            <ModuleToggleList
              tree={tree}
              selectedIds={selectedActionIds}
              onChange={handleModuleChange}
              lockedIds={lockedActionIds}
              disabled={overridesSaving}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

type ModuleManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  tree: IPermission[];
  onContextMenu: (node: IPermission | null, event: React.MouseEvent) => void;
};

const ModuleTreeView: React.FC<{
  tree: IPermission[];
  onContextMenu?: (node: IPermission | null, event: React.MouseEvent) => void;
}> = ({ tree, onContextMenu }) => {
  const hrmsRoot = useMemo(() => {
    const findHrms = (nodes: IPermission[]): IPermission | null => {
      for (const node of nodes) {
        if (
          node.code === "HRMS" &&
          node.type === PermissionNodeType.ROOT
        ) {
          return node;
        }
        if (node.children) {
          const found = findHrms(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findHrms(tree);
  }, [tree]);

  const filterModuleTree = (nodes: IPermission[]): IPermission[] => {
    const filtered: IPermission[] = [];
    nodes.forEach((node) => {
      if (node.type === PermissionNodeType.ACTION) return;
      const children = filterModuleTree(node.children || []);
      filtered.push({ ...node, children });
    });
    return filtered;
  };

  const data = useMemo(() => {
    if (hrmsRoot) {
      return filterModuleTree(hrmsRoot.children || []);
    } else {
      return filterModuleTree(tree);
    }
  }, [tree, hrmsRoot]);

  const renderNodes = (
    nodes: IPermission[],
    depth = 0,
    parentIsLast = false
  ) => {
    return nodes.map((node, idx) => {
      const isLast = idx === nodes.length - 1;

      return (
        <li key={node.id} className="py-1">
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onContextMenu={(e) => {
              e.preventDefault();
              onContextMenu?.(node, e);
            }}
          >
            <div className="flex items-center space-x-2 flex-1">
              <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
              <span className="text-sm font-medium text-slate-700">
                {node.name}
              </span>
            </div>
          </div>
          {node.children && node.children.length > 0 && (
            <ul className="list-none m-0 p-0 ml-4">
              {renderNodes(node.children, depth + 1, isLast)}
            </ul>
          )}
        </li>
      );
    });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white max-h-[60vh] overflow-hidden">
      <div className="bg-primary-600 text-white px-4 py-3 flex items-center">
        <div className="flex items-center space-x-2">
          <Network className="w-4 h-4" />
          <h3 className="text-sm font-semibold">Module Hierarchy</h3>
        </div>
      </div>
      <div className="max-h-[55vh] overflow-auto p-4">
        <ul className="list-none m-0 space-y-1">
          {hrmsRoot ? (
            <li className="py-1">
              <div className="flex items-center space-x-2 cursor-pointer"
                onContextMenu={(e) => {
                  e.preventDefault();
                  onContextMenu?.(null, e);
                }}
              >
                <div className="flex items-center space-x-2 flex-1">
                  <div className="w-2 h-2 rounded-full bg-primary-600"></div>
                  <span className="text-sm font-semibold text-slate-900">
                    {hrmsRoot.name}
                  </span>
                </div>
              </div>
              <ul className="list-none m-0 p-0 ml-4">
                {renderNodes(data, 1, false)}
              </ul>
            </li>
          ) : (
            renderNodes(data, 0, false)
          )}
        </ul>
      </div>
    </div>
  );
};

export const ModuleManagementModal: React.FC<ModuleManagementModalProps> = ({
  isOpen,
  onClose,
  tree,
  onContextMenu,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title="Module Management"
    size="3xl"
    footer={
      <ModalFooter>
        <ModalButton variant="primary" onClick={onClose}>
          Done
        </ModalButton>
      </ModalFooter>
    }
  >
    <ModuleTreeView tree={tree} onContextMenu={onContextMenu} />
  </Modal>
);

type RoleViewModalProps = {
  isOpen: boolean;
  loading: boolean;
  viewRole: RoleRecord | null;
  viewNodeIds: string[];
  tree: IPermission[];
  onClose: () => void;
};

export const RoleViewModal: React.FC<RoleViewModalProps> = ({
  isOpen,
  loading,
  viewRole,
  viewNodeIds,
  tree,
  onClose,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={viewRole ? `View Role: ${viewRole.name}` : "View Role"}
    size="3xl"
    loading={loading}
    bodyClassName="min-h-[400px] flex flex-col"
  >
    {viewRole ? (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase text-slate-500">Role Name</p>
            <p className="text-base font-medium text-slate-900">
              {viewRole.name}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Role Type</p>
            <p className="text-base text-slate-900">{viewRole.type || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Status</p>
            <p className="text-base text-slate-900">
              {viewRole.isActive !== false ? "Active" : "Inactive"}
            </p>
          </div>
        </div>
        {viewRole.description && (
          <div>
            <p className="text-xs uppercase text-slate-500">Description</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">
              {viewRole.description}
            </p>
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-slate-900 mb-2">Permissions</p>
          <PermissionTree
            tree={tree}
            value={viewNodeIds}
            onChange={() => {}}
            disabled
          />
        </div>
      </div>
    ) : loading ? null : (
      <p className="text-sm text-slate-500">No role data available.</p>
    )}
  </Modal>
);

type RoleEditModalProps = {
  isOpen: boolean;
  loading: boolean;
  saving: boolean;
  tree: IPermission[];
  editRole: RoleRecord | null;
  editRoleName: string;
  editRoleType: RoleType | "";
  editRoleDesc: string;
  editIsActive: boolean;
  editNodeIds: string[];
  onClose: () => void;
  onSave: () => void;
  onRoleNameChange: (value: string) => void;
  onRoleTypeChange: (value: RoleType | "") => void;
  onRoleDescChange: (value: string) => void;
  onRoleStatusChange: (value: boolean) => void;
  onNodeIdsChange: (ids: string[]) => void;
};

const editRoleTypeOptions: SelectOption[] = [
  { value: "Admin", label: "Admin" },
  { value: "HR", label: "HR" },
  { value: "Employee", label: "Employee" },
  { value: "Manager", label: "Manager" },
];

export const RoleEditModal: React.FC<RoleEditModalProps> = ({
  isOpen,
  loading,
  saving,
  tree,
  editRole,
  editRoleName,
  editRoleType,
  editRoleDesc,
  editIsActive,
  editNodeIds,
  onClose,
  onSave,
  onRoleNameChange,
  onRoleTypeChange,
  onRoleDescChange,
  onRoleStatusChange,
  onNodeIdsChange,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={editRole ? `Edit Role: ${editRole.name}` : "Edit Role"}
    size="xl"
    loading={loading}
    footer={
      <ModalFooter>
        <ModalButton variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </ModalButton>
        <ModalButton
          variant="primary"
          onClick={onSave}
          loading={saving}
          disabled={loading}
        >
          Save Changes
        </ModalButton>
      </ModalFooter>
    }
  >
    {editRole ? (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Role Name"
            required
            placeholder="Enter role name"
            value={editRoleName}
            onChange={(value) => onRoleNameChange(value as string)}
            disabled={loading || saving}
          />
          <Select
            label="Role Type"
            required
            options={editRoleTypeOptions}
            value={editRoleType}
            onChange={(value) => onRoleTypeChange(value as RoleType | "")}
            placeholder="Select type"
            clearable
            disabled={loading || saving}
          />
        </div>
        <div>
          <RadioButton
            name="editRoleActive"
            label="Status"
            required
            value={editIsActive ? "active" : "inactive"}
            onChange={(val) => onRoleStatusChange(val === "active")}
            options={[
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ]}
            disabled={loading || saving}
          />
        </div>
        <div className="space-y-2">
          <TextArea
            label="Description"
            placeholder="Optional notes about this role"
            value={editRoleDesc}
            onChange={(value) => onRoleDescChange(value)}
            minRows={3}
            disabled={loading || saving}
          />
        </div>
        <div className="border-t border-[var(--color-border-primary)] pt-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Module Permissions
              </h4>
              <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                Toggle modules on/off to grant or revoke all actions under each module.
              </p>
            </div>
          </div>
          <ModuleToggleList
            key={editRole?.id}
            tree={tree}
            selectedIds={editNodeIds}
            onChange={onNodeIdsChange}
            disabled={loading || saving}
          />
        </div>
      </div>
    ) : (
      <p className="text-sm text-[var(--color-text-secondary)]">No role selected.</p>
    )}
  </Modal>
);

type DeleteRoleModalProps = {
  isOpen: boolean;
  deleteTarget: RoleRecord | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
};

export const DeleteRoleModal: React.FC<DeleteRoleModalProps> = ({
  isOpen,
  deleteTarget,
  onClose,
  onConfirm,
  loading,
}) => (
  <ConfirmationModal
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Delete Role"
    message={
      deleteTarget
        ? `Are you sure you want to delete the "${deleteTarget.name}" role?`
        : "Are you sure you want to delete this role?"
    }
    type="danger"
    confirmText="Delete"
    cancelText="Cancel"
    isLoading={loading}
  />
);

type DeleteBlockedInfo = {
  roleName: string;
  assignedUserCount: number;
  assignedUsers: Array<{
    id: string;
    name?: string;
    email?: string;
    workEmail?: string;
    username?: string;
  }>;
};

type DeleteBlockedModalProps = {
  info: DeleteBlockedInfo | null;
  onClose: () => void;
};

export const DeleteBlockedModal: React.FC<DeleteBlockedModalProps> = ({
  info,
  onClose,
}) => (
  <Modal
    isOpen={!!info}
    onClose={onClose}
    title="Role Assigned To Users"
    size="md"
  >
    {info ? (
      <div className="space-y-4">
        <p className="text-sm text-slate-700">
          The role <span className="font-semibold">{info.roleName}</span> is
          currently assigned to{" "}
          <span className="font-semibold">{info.assignedUserCount}</span>{" "}
          {info.assignedUserCount === 1 ? "user" : "users"}. Please reassign or
          remove the role from these users before attempting to deactivate it.
        </p>
        <div>
          <p className="text-xs uppercase text-slate-500 mb-2">
            Sample Assigned Users
          </p>
          <ul className="space-y-2">
            {(info.assignedUsers || []).map((user) => (
              <li key={user.id} className="flex flex-col text-sm text-slate-800">
                <span>
                  {user.name ||
                    user.username ||
                    user.workEmail ||
                    "Unnamed User"}
                </span>
                {user.workEmail && (
                  <span className="text-xs text-slate-500">
                    {user.workEmail}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {info.assignedUserCount > (info.assignedUsers || []).length && (
            <p className="text-xs text-slate-500 mt-1">
              &hellip;and {info.assignedUserCount - (info.assignedUsers || []).length}{" "}
              more.
            </p>
          )}
        </div>
        <div className="flex justify-end">
          <ModalButton variant="primary" onClick={onClose}>
            Got it
          </ModalButton>
        </div>
      </div>
    ) : (
      <p className="text-sm text-slate-500">No assignment details available.</p>
    )}
  </Modal>
);


